/**
 * Pure public-id parsing utilities.
 *
 * Every resource in the system mints a public id of the form
 * `<prefix>_<random>` (the random suffix is a 24-char hex slice of a UUID;
 * see the `'<prefix>_' || substring(replace(gen_random_uuid()::text …))`
 * defaults in `packages/db/src/schema.ts`). The prefix alone identifies
 * which resource a bare id refers to — `tsk_…` is a task, `ext_…` an
 * extractor, `fil_…` a file, and so on.
 *
 * This module is the canonical, framework-free registry of that
 * prefix → resource-type mapping plus the pure helpers that consume it:
 * parse an id into `{ prefix, type }`, narrow with type guards, and ask
 * "is this id of type X". It lives in `@quovy/types` (not in
 * `@quovy/db`, which depends on this package) so the frontend, API,
 * workers, and server helpers can all share one source of truth without
 * pulling in Drizzle or the schema.
 *
 * Source of truth & drift protection: `packages/db/src/schema.ts` owns the
 * actual SQL prefix defaults; this registry mirrors them. Because
 * `@quovy/types` cannot import the schema, the cross-check is enforced by
 * `packages/db/src/public-id-registry-sync.test.ts`, which parses the
 * schema's `'<prefix>_' || …` defaults and asserts every minted prefix is
 * registered here. Add a new prefix to `PUBLIC_ID_PREFIXES` whenever a new
 * `pgTable` mints public ids, or that guard test will fail.
 */

/**
 * The closed set of public-id prefixes, keyed by prefix (without the
 * trailing underscore the on-the-wire id carries — e.g. `"tsk"`, not
 * `"tsk_"`). The value is the resource type the prefix identifies.
 *
 * Keep this in sync with the `'<prefix>_' || …` `public_id` defaults in
 * `packages/db/src/schema.ts`. The guard test
 * `public-id-registry-sync.test.ts` fails the build on drift.
 */
export const PUBLIC_ID_PREFIXES = {
  aeg: "agent_execution_group",
  adl: "agent_delegation",
  usr: "user",
  org: "organization",
  orm: "organization_membership",
  dom: "organization_domain",
  du: "directory_user",
  dg: "directory_group",
  dgm: "directory_group_workspace_mapping",
  wmg: "workspace_membership_grant",
  omg: "organization_membership_grant",
  wsp: "workspace",
  wci: "workspace_configuration_import",
  wcd: "workspace_configuration_draft",
  rel: "workspace_release",
  rla: "workspace_release_activation",
  wga: "workspace_github_authorization",
  wgc: "workspace_github_connection",
  wgo: "workspace_github_operation",
  wgr: "workspace_github_revision",
  wcb: "workspace_configuration_binding",
  wsm: "workspace_membership",
  fil: "file",
  sdc: "search_document",
  sde: "search_document_embedding",
  pdc: "file_parse",
  tsm: "triage_summary",
  key: "api_key",
  oac: "oauth_client",
  ooc: "outbound_oauth_connection",
  oak: "oauth_client_key",
  oug: "oauth_user_grant",
  sog: "staff_ops_grant",
  rql: "api_request_log",
  wfd: "workflow_definition",
  wdv: "workflow_definition_version",
  wfr: "workflow_run",
  wst: "workflow_run_step",
  ene: "extraction_runtime",
  ext: "extractor",
  exv: "extractor_version",
  cls: "classifier",
  clv: "classifier_version",
  exr: "extraction_run",
  clr: "classification_run",
  fcl: "classification",
  cev: "cost_event",
  era: "run_attempt",
  tsk: "task",
  tpt: "task_participant",
  wat: "watcher",
  tcm: "task_comment",
  tev: "task_event",
  ttpl: "task_template",
  lnk: "linked_resource",
  sbs: "sandbox_session",
  agt: "agent",
  agv: "agent_version",
  agb: "agent_version_bundle",
  skl: "skill",
  gsr: "global_skill_release",
  ath: "agent_thread",
  amg: "agent_message",
  aqm: "agent_thread_queued_message",
  lsn: "agent_thread_listener",
  lsf: "agent_thread_listener_fire",
  air: "agent_thread_input_request",
  aul: "agent_usage_log",
  cuf: "computer_use_frame",
  epol: "email_policy",
  eprl: "email_policy_rule",
  epat: "email_policy_attachment",
  emsg: "inbound_email",
  eth: "email_thread",
  ede: "email_delivery_event",
  sig: "signal",
  sar: "system_agent_run",
  azc: "analyzer_recommendation",
  amem: "agent_memory",
  brg: "bridge",
  rdt: "remote_desktop_target",
  rdd: "remote_desktop_desk",
  int: "integration",
  lac: "linked_account",
  imb: "integration_mailbox",
  mbf: "mailbox_folder",
  sps: "sharepoint_integration_site",
  igr: "integration_grant",
  iie: "integration_inbound_endpoint",
  irs: "integration_resource_scope",
  iev: "integration_event",
  iin: "source_inbox",
  scu: "source_cursor",
  vlc: "vault_credential",
  vle: "vault_event",
  inv: "invitation",
  eds: "eval_dataset",
  edi: "eval_dataset_item",
  scn: "eval_scenario",
  scnv: "eval_scenario_version",
  scns: "eval_scenario_suite",
  scnsv: "eval_scenario_suite_version",
  ean: "eval_annotation",
  eve: "eval_evaluator",
  evev: "eval_evaluator_version",
  efx: "eval_fixture_set",
  efxv: "eval_fixture_revision",
  eex: "eval_experiment",
  ecm: "eval_experiment_comment",
  eer: "eval_experiment_result",
  tee: "tool_execution_environment",
  evt: "event",
  whe: "webhook_endpoint",
  wda: "webhook_delivery_attempt",
  exp: "export",
  ntf: "notification",
  ndv: "notification_delivery",
  npf: "notification_preference",
  sched: "schedule",
  rtn: "routine",
  cmdl: "custom_model",
  crec: "custom_record",
  crch: "custom_record_change",
  tag: "tag",
  tgr: "tag_resource",
  aer: "agent_eval_run",
  aes: "agent_eval_scenario",
  axr: "agent_eval_result",
  grd: "grader",
  gra: "grade",
  iss: "issue",
  sug: "suggestion",
  sgc: "suggestion_change",
  vrf: "verification",
  vrr: "verification_run",
  vfd: "verification_field",

  // EXPERIMENTAL custom apps (delete-or-graduate; docs/exec-plans/active/custom-apps-spike.md)
  app: "custom_app",
  ajr: "custom_app_job_run",
  apv: "custom_app_version",

  // Persistent per-user agent computers (experimental) (docs/roadmap/persistent-agent-computers.md)
  sbn: "sandbox_snapshot",
  agc: "agent_credential",

  // Control plane (packages/db/src/control-plane-schema.ts). These identify
  // Quovy's own infrastructure — environments, releases, deployments — not
  // tenant-owned resources, and are minted in a separate schema file from the
  // product tables. `env` was already taken, hence the `cp*` family.
  cpe: "control_plane_environment",
  cpr: "control_plane_release",
  cpd: "control_plane_deployment",
  cph: "control_plane_heartbeat",
  cpp: "control_plane_provisioning_run",
  cpo: "control_plane_customer_organization",
  cpu: "control_plane_customer_user",
} as const;

/** A registered prefix, without the trailing underscore (e.g. `"tsk"`). */
export type PublicIdPrefix = keyof typeof PUBLIC_ID_PREFIXES;

/** The resource type a prefix identifies (e.g. `"task"`). */
export type PublicIdResourceType = (typeof PUBLIC_ID_PREFIXES)[PublicIdPrefix];

/**
 * Reverse map: resource type → prefix. Built once at module load. Useful
 * for "what prefix does a `task` carry" lookups and for asserting an
 * id matches an expected resource type.
 */
export const RESOURCE_TYPE_TO_PREFIX = Object.freeze(
  Object.fromEntries(Object.entries(PUBLIC_ID_PREFIXES).map(([prefix, type]) => [type, prefix])),
) as Readonly<Record<PublicIdResourceType, PublicIdPrefix>>;

/** Every registered prefix, in registry order. */
export const ALL_PUBLIC_ID_PREFIXES = Object.freeze(
  Object.keys(PUBLIC_ID_PREFIXES),
) as readonly PublicIdPrefix[];

/** Every registered resource type, in registry order. */
export const ALL_PUBLIC_ID_RESOURCE_TYPES = Object.freeze(
  Object.values(PUBLIC_ID_PREFIXES),
) as readonly PublicIdResourceType[];

/** A successfully parsed public id. */
export type ParsedPublicId = {
  /** The full id exactly as passed in (e.g. `"tsk_3f…"`). */
  readonly id: string;
  /** The prefix without the trailing underscore (e.g. `"tsk"`). */
  readonly prefix: PublicIdPrefix;
  /** The resource type the prefix maps to (e.g. `"task"`). */
  readonly type: PublicIdResourceType;
  /** The random suffix after the first underscore (e.g. `"3f…"`). */
  readonly suffix: string;
};

/**
 * Split a candidate id into its prefix and suffix without consulting the
 * registry. Returns `null` when the shape is not `<prefix>_<suffix>` with
 * both parts non-empty. Exported for callers that want to inspect the
 * shape of an unknown / unregistered id (e.g. for error messages).
 */
export function splitPublicId(candidate: string): { prefix: string; suffix: string } | null {
  const underscoreIdx = candidate.indexOf("_");
  // Reject leading-underscore ids and ids with no suffix.
  if (underscoreIdx <= 0 || underscoreIdx >= candidate.length - 1) return null;
  return {
    prefix: candidate.slice(0, underscoreIdx),
    suffix: candidate.slice(underscoreIdx + 1),
  };
}

/**
 * Length of the random suffix every minted public id carries — the first 24
 * hex characters of a UUID, per the `public_id` column defaults in
 * packages/db/src/schema.ts.
 */
export const MINTED_PUBLIC_ID_SUFFIX_LENGTH = 24;

/**
 * Full shape of a minted public id: `<prefix>_<24 lowercase hex>`. The ONE
 * definition of that shape — import it rather than re-spelling the regex.
 *
 * {@link parsePublicId} deliberately does not apply this: it accepts any
 * `<registered prefix>_<non-empty suffix>` so that hand-written test ids keep
 * resolving. Every tool and API input applies the strict form through
 * `zPublicId`, so a truncated or junk-suffixed id is rejected, not looked up.
 */
export const MINTED_PUBLIC_ID_PATTERN = /^[a-z]+_[0-9a-f]{24}$/;

const LEADING_MINTED_PUBLIC_ID = /^([a-z]+_[0-9a-f]{24})(?![0-9a-f])./s;

/**
 * The minted id `candidate` starts with when other characters follow it, e.g.
 * `cmdl_<24 hex>` out of `cmdl_<24 hex>}}]}` from a garbled parallel tool call.
 * Null when the body runs on in hex, since the intended id is then unknowable.
 */
export function leadingMintedPublicId(candidate: string): string | null {
  return LEADING_MINTED_PUBLIC_ID.exec(candidate)?.[1] ?? null;
}

let readDeclaredPublicIds: () => ReadonlySet<string> | undefined = () => undefined;

/**
 * A simulation fixture may name ids outside the minted shape (`eth_claim`).
 * The simulated run installs a reader of the ids its fixture declares so they
 * stay valid inputs there; with no reader installed nothing is declared.
 */
export function setDeclaredPublicIdsReader(reader: () => ReadonlySet<string> | undefined): void {
  readDeclaredPublicIds = reader;
}

/** Is `candidate` an id the current simulated run's fixture declares? */
export function isDeclaredPublicId(candidate: string): boolean {
  return readDeclaredPublicIds()?.has(candidate) ?? false;
}

/** Is `candidate` shaped like a minted public id? Says nothing about the prefix registry. */
/** Matches every minted public id in a text. Global, so each scan takes its own instance. */
export function mintedPublicIdPattern(): RegExp {
  return new RegExp(
    `\\b(?:${ALL_PUBLIC_ID_PREFIXES.join("|")})_[0-9a-f]{${MINTED_PUBLIC_ID_SUFFIX_LENGTH}}\\b`,
    "g",
  );
}

export function isMintedPublicId(candidate: string): boolean {
  return MINTED_PUBLIC_ID_PATTERN.test(candidate);
}

/**
 * Type guard: is `candidate` a *fully formed* public id of the given resource
 * type — registered prefix AND the exact minted suffix shape? The strict
 * counterpart to {@link isPublicIdOfType}, for boundaries that must reject a
 * truncated or mistyped id instead of storing it.
 */
export function isMintedPublicIdOfType<T extends PublicIdResourceType>(
  candidate: string,
  type: T,
): candidate is PublicId<T> {
  return isMintedPublicId(candidate) && isPublicIdOfType(candidate, type);
}

/**
 * Type guard: is `candidate` a registered public-id prefix (without the
 * trailing underscore)?
 */
export function isPublicIdPrefix(candidate: string): candidate is PublicIdPrefix {
  return Object.prototype.hasOwnProperty.call(PUBLIC_ID_PREFIXES, candidate);
}

/**
 * Parse a public id into `{ id, prefix, type, suffix }`. Returns `null`
 * when the input is not a well-formed `<prefix>_<suffix>` string OR the
 * prefix is not registered. Pure — does no I/O and never throws.
 *
 * Use this at trust boundaries where the id may be malformed or of an
 * unexpected type. Prefer the narrower {@link isPublicIdOfType} when you
 * only need a yes/no for a single expected type.
 */
export function parsePublicId(candidate: string): ParsedPublicId | null {
  const split = splitPublicId(candidate);
  if (!split) return null;
  if (!isPublicIdPrefix(split.prefix)) return null;
  return {
    id: candidate,
    prefix: split.prefix,
    type: PUBLIC_ID_PREFIXES[split.prefix],
    suffix: split.suffix,
  };
}

/**
 * Resolve a public id to its resource type, or `null` for malformed /
 * unregistered ids. Thin convenience over {@link parsePublicId} for
 * callers that only need the type.
 */
export function resourceTypeFromPublicId(candidate: string): PublicIdResourceType | null {
  return parsePublicId(candidate)?.type ?? null;
}

/**
 * Branded public id for a single resource type — `PublicId<"task">`
 * is a `string` that has been verified to carry the `tsk_` prefix. Use it
 * to thread "this string is definitely a task id" through the type
 * system after a guard.
 */
export type PublicId<T extends PublicIdResourceType> = string & {
  readonly __publicIdType: T;
};

/**
 * Type guard: is `candidate` a public id of the given resource type?
 * Narrows to the branded `PublicId<T>` so downstream code can rely on the
 * type without re-checking.
 *
 * ```ts
 * if (isPublicIdOfType(id, "task")) {
 *   // id: PublicId<"task">
 * }
 * ```
 */
export function isPublicIdOfType<T extends PublicIdResourceType>(
  candidate: string,
  type: T,
): candidate is PublicId<T> {
  const parsed = parsePublicId(candidate);
  return parsed !== null && parsed.type === type;
}

/**
 * Assert that `candidate` is a public id of the given resource type,
 * returning it branded. Throws a descriptive error otherwise. Use inside
 * the system where the wrong type indicates a contract violation upstream;
 * prefer {@link isPublicIdOfType} at boundaries where a mismatch is a
 * normal "reject the request" case.
 */
export function assertPublicIdOfType<T extends PublicIdResourceType>(
  candidate: string,
  type: T,
): PublicId<T> {
  if (!isPublicIdOfType(candidate, type)) {
    const actual = resourceTypeFromPublicId(candidate);
    const detail = actual ? `it is a ${actual} id` : `it is not a recognized public id`;
    throw new Error(
      `assertPublicIdOfType: expected a ${type} id, but ${detail}: ${JSON.stringify(candidate)}`,
    );
  }
  return candidate;
}
